import { useEffect, useState } from 'react';

const MAPS_SCRIPT_SELECTOR = '#google-maps-script, script[src*="maps.googleapis.com/maps/api/js"]';

// The app shell loads the Maps script with `loading=async`. That bootstrap
// defines google.maps.importLibrary — and fires the script's `load` event —
// while the actual libraries do NOT exist yet. So testing only
// `window.google.maps` reports ready too early: @react-google-maps/api then
// calls `new google.maps.Map(...)` synchronously in componentDidMount and
// throws, and react-google-autocomplete bails with "Google maps places API must
// be loaded." Readiness must mean "the thing callers construct exists".
//
// One probe per library, checking the constructor each consumer actually uses.
const LIBRARY_PROBES = {
    maps: (maps) => typeof maps.Map === 'function',
    places: (maps) => typeof maps.places?.Autocomplete === 'function',
    geometry: (maps) => Boolean(maps.geometry),
    drawing: (maps) => Boolean(maps.drawing),
};

const mapsNamespace = () => (typeof window !== 'undefined' && window.google && window.google.maps) || null;

const isLibraryReady = (library) => {
    const maps = mapsNamespace();
    if (!maps) return false;
    const probe = LIBRARY_PROBES[library] || LIBRARY_PROBES.maps;
    try {
        return probe(maps);
    } catch (e) {
        return false;
    }
};

const canImportLibrary = () => {
    const maps = mapsNamespace();
    return Boolean(maps && typeof maps.importLibrary === 'function');
};

/**
 * Readiness of one Google Maps library, consuming the single app-shell script.
 * Never injects a competing copy.
 *
 * @param {'maps'|'places'|'geometry'|'drawing'} library defaults to 'maps'
 */
export default function useGoogleMapsReady(library = 'maps') {
    const [isLoaded, setIsLoaded] = useState(() => isLibraryReady(library));
    const [loadError, setLoadError] = useState();

    useEffect(() => {
        if (isLibraryReady(library)) {
            setLoadError(undefined);
            setIsLoaded(true);
            return undefined;
        }

        const script = document.querySelector(MAPS_SCRIPT_SELECTOR);
        if (!script) {
            setLoadError(new Error('Google Maps app-shell script is missing.'));
            return undefined;
        }

        let cancelled = false;

        // Called once the bootstrap is present. Under `loading=async` the real
        // library still has to be pulled in via importLibrary.
        const resolveLibrary = () => {
            if (cancelled) return;
            if (isLibraryReady(library)) {
                setLoadError(undefined);
                setIsLoaded(true);
                return;
            }
            if (!canImportLibrary()) {
                setLoadError(new Error('Google Maps script loaded without exposing the Maps API.'));
                return;
            }
            Promise.resolve(window.google.maps.importLibrary(library))
                .then(() => {
                    if (cancelled) return;
                    if (isLibraryReady(library)) {
                        setLoadError(undefined);
                        setIsLoaded(true);
                    } else {
                        setLoadError(new Error(`Google Maps resolved without the ${library} library.`));
                    }
                })
                .catch(() => {
                    if (!cancelled) setLoadError(new Error(`Google Maps ${library} library failed to load.`));
                });
        };

        const handleError = () => {
            if (!cancelled) setLoadError(new Error('Google Maps app-shell script failed to load.'));
        };

        script.addEventListener('load', resolveLibrary);
        script.addEventListener('error', handleError);

        // A cached shell script fires `load` once, before any page mounts, so
        // waiting for the event alone would hang forever. Only try immediately
        // when the bootstrap is actually there — otherwise the script is still
        // in flight and the listener is the right path.
        if (mapsNamespace()) resolveLibrary();

        return () => {
            cancelled = true;
            script.removeEventListener('load', resolveLibrary);
            script.removeEventListener('error', handleError);
        };
    }, [library]);

    return { isLoaded, loadError };
}
